import {Box, Button, FormHelperText, Grid, MenuItem, Stack, TextField, Tooltip, Typography} from "@mui/material";
import BasicDateTimePicker from "../../Utils/BasicDateTimePicker";
import dayjs from "dayjs";
import {
    formatDuration,
    formatDurationHours,
    formatDurationSeconds,
    getRecommendedDurationSeconds,
    QUICK_DURATION_DAYS
} from "../CampaignDuration";
import {TIME_ZONE, getAnnounceFrom, getExtraTill, getTemplateRuns} from "../campaignFormModel";

export const timeZoneOffset = TIME_ZONE;

const DISTRIBUTION_TYPES = [
    {displayName: "FIXED", value: "fixed"},
    {displayName: "PERMANENT", value: "permanent"},
    {displayName: "FLOAT", value: "float"},
];

export default function DateStep(props) {
    const {currentCampaign, errorFields, typeParameters} = props;
    const recommendedDuration = getRecommendedDurationSeconds(typeParameters);
    const isPermanent = currentCampaign.distributionType === 'permanent';
    const durationDisabled = currentCampaign.distributionType === 'fixed' || isPermanent;
    const runs = props.template ? getTemplateRuns(currentCampaign) : null;
    const getFrom = () => {
        const from = dayjs(currentCampaign.from).utcOffset(TIME_ZONE.value);
        return from.isValid() ? from : dayjs().utcOffset(TIME_ZONE.value);
    };

    const handleDayShortcut = (daysAgo) => {
        const from = getFrom();
        const date = dayjs().utcOffset(TIME_ZONE.value).subtract(daysAgo, 'day')
            .hour(from.hour()).minute(from.minute()).second(from.second()).millisecond(from.millisecond());
        props.handleDateChange(date, 'from');
    };

    return (
        <Grid container spacing={2}>
            <Grid item xs={6}>
                <BasicDateTimePicker
                    label="From (GMT+3)"
                    name="from"
                    value={currentCampaign.from}
                    onChange={props.handleDateChange}
                />
                <Stack direction="row" spacing={1} sx={{mt: 1}}>
                    <Button size="small" variant="outlined" onClick={() => handleDayShortcut(1)}>
                        Yesterday
                    </Button>
                    <Button size="small" variant="outlined" onClick={() => handleDayShortcut(0)}>
                        Today
                    </Button>
                </Stack>
            </Grid>
            <Grid item xs={6}>
                <BasicDateTimePicker
                    label="Till (GMT+3)"
                    name="till"
                    value={currentCampaign.till}
                    onChange={props.handleDateChange}
                    disabled={isPermanent}
                />
                {errorFields.has('date') &&
                    <FormHelperText error>The end date must be after the start date</FormHelperText>}
                <Typography variant="body2" color="text.secondary" sx={{mt: 1}}>
                    Distribution window: {isPermanent ? 'Permanent' : formatDuration(currentCampaign.from, currentCampaign.till)}
                </Typography>
                <Stack direction="row" spacing={1} useFlexGap sx={{mt: 1, flexWrap: 'wrap'}}>
                    {QUICK_DURATION_DAYS.map(days => (
                        <Button
                            key={days}
                            size="small"
                            variant="outlined"
                            disabled={isPermanent}
                            onClick={() => props.handleTillDurationChange(days)}
                        >
                            {days} {days === 1 ? 'day' : 'days'}
                        </Button>
                    ))}
                    {recommendedDuration !== null &&
                        <Tooltip title={`Recommended duration: ${formatDurationSeconds(recommendedDuration)}`}>
                            <span>
                                <Button
                                    size="small"
                                    variant="outlined"
                                    sx={{whiteSpace: 'nowrap'}}
                                    disabled={isPermanent}
                                    onClick={() => props.handleRecommendedDurationChange(recommendedDuration)}
                                >
                                    Recommended
                                </Button>
                            </span>
                        </Tooltip>}
                </Stack>
            </Grid>
            <Grid item xs={6}>
                <BasicDateTimePicker
                    disabled={true}
                    label="Announce From (GMT+3)"
                    name="announceDuration"
                    value={getAnnounceFrom(currentCampaign, typeParameters)}
                />
            </Grid>
            <Grid item xs={6}>
                <BasicDateTimePicker
                    disabled={true}
                    label="Extra Time Till (GMT+3)"
                    name="extraDuration"
                    value={getExtraTill(currentCampaign, typeParameters)}
                />
            </Grid>
            <Grid item xs={6}>
                <TextField
                    required
                    id="distributionType"
                    name="distributionType"
                    label="Distribution Type"
                    error={errorFields.has('distributionType')}
                    select
                    fullWidth
                    value={currentCampaign.distributionType}
                    onChange={props.handleChange}
                >
                    {DISTRIBUTION_TYPES.map((option) => (
                        <MenuItem key={option.value} value={option.value}>{option.displayName}</MenuItem>
                    ))}
                </TextField>
            </Grid>
            <Grid item xs={6}>
                <TextField
                    required={!durationDisabled}
                    id="durationHour"
                    name="durationHour"
                    label="Duration (Hours)"
                    disabled={durationDisabled}
                    fullWidth
                    error={errorFields.has('durationHour')}
                    value={currentCampaign.durationHour}
                    onChange={props.handleChange}
                    helperText={!durationDisabled && currentCampaign.durationHour !== ''
                        ? `Duration: ${formatDurationHours(currentCampaign.durationHour)}` : undefined}
                />
            </Grid>

            {props.template && <>
                <Grid item xs={12}>
                    <Typography variant="subtitle1" sx={{fontWeight: 600}}>Template schedule</Typography>
                </Grid>
                <Grid item xs={12}>
                    <TextField
                        required
                        id="delayDays"
                        name="delayDays"
                        label="Delay Days"
                        error={errorFields.has('delayDays')}
                        value={currentCampaign.schedule.delayDays}
                        fullWidth
                        onChange={props.handleChangeSchedule}
                    />
                </Grid>
                {runs && <Grid item xs={12}>
                    <Box sx={{color: 'text.secondary', typography: 'body2'}}>
                        Next campaign example: {runs.next.date} - {runs.next.pair}
                        {runs.afterNext && <>, {runs.afterNext.date} - {runs.afterNext.pair} …</>}
                    </Box>
                </Grid>}
                <Grid item xs={12}>
                    <BasicDateTimePicker
                        label="Till (GMT+3)"
                        name="till"
                        value={currentCampaign.schedule.till}
                        onChange={props.handleChangeScheduleTill}
                        minDate={dayjs(currentCampaign.from).add(currentCampaign.schedule.delayDays, 'day')}
                    />
                    {errorFields.has('scheduletill') &&
                        <FormHelperText error>The TILL schedule date must be after the start date</FormHelperText>}
                </Grid>
                {runs && currentCampaign.schedule.till !== null && <Grid item xs={12}>
                    <Box sx={{color: 'text.secondary', typography: 'body2'}}>
                        Last campaign dates: {runs.last.date} - {runs.last.pair}
                    </Box>
                </Grid>}
            </>}
        </Grid>
    );
}
